import type { PageContext } from '@teo/widget-shared';
import { currentNavigationEpoch, waitForNavigationRecollection } from './navigation-epoch.js';
import { resolvePageEvidence } from './resolve-page-evidence.js';
import { collectUrlEvidence } from './sources/url-heuristics.js';
import { mergeBehaviorIntoContext } from './merge-behavior.js';
import { mergeCartIntoContext } from './cart/merge-cart.js';

export type CollectPageContextOptions = {
  doc?: Document;
  url?: string;
  /** Skip merging live behavior snapshot (unit tests). */
  skipBehavior?: boolean;
  /** Skip merging live cart snapshot (cart watchers / unit tests). */
  skipCart?: boolean;
};

type ParserImpl = typeof import('./parse-page-context-impl.js');

let parserPromise: Promise<ParserImpl> | null = null;
let parserModule: ParserImpl | null = null;

/** Preload full parser when CONTEXT_ENABLED (phase 5.8). */
export function ensurePageContextParserLoaded(): Promise<ParserImpl> {
  if (parserModule) return Promise.resolve(parserModule);
  if (!parserPromise) {
    parserPromise = import('./parse-page-context-impl.js').then((mod) => {
      parserModule = mod;
      return mod;
    });
  }
  return parserPromise;
}

function safePathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return '/';
  }
}

/** Minimal snapshot when the heavy parser chunk is not loaded yet. */
function collectMinimalPageContext(
  options: CollectPageContextOptions = {},
): PageContext {
  const doc = options.doc ?? document;
  const url =
    options.url ?? (typeof window !== 'undefined' ? window.location.href : '');
  const pathname = safePathname(url);
  const title = doc.title?.trim() || pathname;
  const context = { doc, url, pathname, epoch: currentNavigationEpoch(url).epoch };
  const decision = resolvePageEvidence(context, collectUrlEvidence(context));

  return {
    url,
    pathname,
    title,
    pageType: decision.type,
    classification: { confidence: decision.confidence, reason: decision.reason, evidenceIds: decision.evidenceIds },
    sources: ['minimal'],
    collectedAt: new Date().toISOString(),
  };
}

function applyLiveMerges(
  base: PageContext,
  options: CollectPageContextOptions,
): PageContext {
  let next = base;
  if (!options.skipBehavior) next = mergeBehaviorIntoContext(next);
  if (!options.skipCart) next = mergeCartIntoContext(next);
  return next;
}

/**
 * Collect structured page context. Uses full cascade when parser chunk is loaded;
 * otherwise returns a minimal snapshot (title + URL heuristics only).
 */
export function collectPageContext(
  options: CollectPageContextOptions = {},
): PageContext {
  const base = parserModule
    ? parserModule.collectPageContextImpl(options)
    : collectMinimalPageContext(options);

  return applyLiveMerges(base, options);
}

/** Async collector — full cascade when `fullContext` is true (default). */
export async function collectPageContextAsync(
  options: CollectPageContextOptions & { fullContext?: boolean } = {},
): Promise<PageContext> {
  if (options.fullContext === false) {
    return collectPageContext({
      ...options,
      skipBehavior: true,
      skipCart: true,
    });
  }
  await ensurePageContextParserLoaded();
  const url = options.url ?? (typeof window !== 'undefined' ? window.location.href : '');
  await waitForNavigationRecollection(url);
  return collectPageContext(options);
}
